import { makeAutoObservable, runInAction, reaction, type IReactionDisposer } from 'mobx'
import { nanoid } from 'nanoid'
import { logger } from 'shared/lib'
import { TableStore } from 'shared/ui/TableNew/store/tableStore'
import { showToast, Typography } from 'shared/ui'
import modalStore from 'shared/ui/Modal/store/modalStore'
import { ModalTypeList } from 'shared/ui/Modal/store/types'

import { uiStore } from 'shared/store/uiStore'
import { RequestLoadData } from 'shared/api/lib'
import type { IEntitiesStore } from 'entities/EntitiesExport'
import { type IResponseExternalWebhook, type WebhookRow } from 'entities/Webhook'
import { WebhookApi } from 'entities/Webhook/api/webhook'
import { IntegrationsRoutes } from 'entities/ExportRouters'
import { DetailsModal } from 'pages/settings/pages/integrations/pages/Webhooks/ui/DetailsModal/DetailsModal'
import { WebhookHistoryStore } from 'pages/settings/pages/integrations/pages/Webhooks/store/WebhookHistoryStore'
import styles from './../styles.module.scss'

export class IntegrationWebhooksStore {
  private _webhookHistoryStore: WebhookHistoryStore
  private _webhooks: IResponseExternalWebhook[] = []
  private _firstLoading = true
  private readonly _tableStore: TableStore<WebhookRow>
  private _disposeParamsReaction: IReactionDisposer | null = null
  private _deleteModalId = nanoid()

  constructor(private _entities: IEntitiesStore) {
    makeAutoObservable(this)

    this._webhookHistoryStore = new WebhookHistoryStore(this._entities)
    this._tableStore = new TableStore<WebhookRow>({
      key: 'integration_webhooks',
      element: 'webhook',
      useColumnsCache: false,
      limit: 10,
      page: 1,
      sortBy: 'created_at',
      sortOrder: 'desc',
    })
  }

  init = async () => {
    await Promise.all([this.loadWebhooks(), this._entities.integrationsStore.fetchIntegrations()])
    this._setupParamsReaction()
  }

  private _setupParamsReaction = () => {
    this._disposeParamsReaction?.()
    this._disposeParamsReaction = reaction(
      () => ({
        page: this._tableStore.page,
        limit: this._tableStore.limit,
        sortBy: this._tableStore.sortBy,
        sortOrder: this._tableStore.sortOrder,
      }),
      () => this._requestLoadWebhooks.debounceLoadData()
    )
  }

  private _requestLoadWebhooks = new RequestLoadData({
    debounceDelay: 300,
    requestCallback: async () => {
      runInAction(() => {
        this._tableStore.setLoading(true)
      })

      const { data } = await WebhookApi.getExternalWebhooks({
        sortBy: this._tableStore.sortBy,
        sortOrder: this._tableStore.sortOrder,
        limit: this._tableStore.limit,
        page: this._tableStore.page,
      })

      runInAction(() => {
        this._webhooks = data.data
        this._tableStore.setTotal(data.meta.total)
      })
    },
    finallyCallback: () => {
      runInAction(() => {
        this._tableStore.setLoading(false)
        this._firstLoading = false
      })
    },
  })

  loadWebhooks = async () => {
    return this._requestLoadWebhooks.loadData()
  }

  onEdit = (webhook: IResponseExternalWebhook) => {
    uiStore.changeRoute({
      path: `/${IntegrationsRoutes.settings}/${IntegrationsRoutes.root}/${IntegrationsRoutes.webhooks}/${IntegrationsRoutes.edit}/${webhook.id}`,
    })
  }

  get webhookHistoryStore() {
    return this._webhookHistoryStore
  }

  get webhooksRows(): WebhookRow[] {
    return this._webhooks.map((webhook) => ({
      id: webhook.id,
      name: webhook.name,
      url: webhook.url,
      status: webhook.is_active,
      created_at: webhook.created_at,
      expanded: false,
      is_can_expanded: false,
      isMoreExpand: false,
      loadingMore: false,
      loading: false,
      actionsProps: {
        actions: [
          {
            icon: webhook.is_active ? 'toggleOn' : 'toggleOff',
            text: webhook.is_active ? 'Active' : 'Disabled',
            withDivider: true,
            onAction: () => {
              this.updateStatus(webhook)
            },
          },
          {
            icon: 'edit',
            text: 'Edit',
            onAction: () => {
              this.onEdit(webhook)
            },
          },
          {
            icon: 'eye',
            text: 'Details',
            onAction: () => {
              this.openDetailsModal(webhook)
            },
          },
          {
            icon: 'delete',
            text: 'Delete',
            onAction: () => {
              this.openDeleteModal(webhook)
            },
          },
        ],
      },
      actions: null,
      isDisabled: false,
      isCheckboxDisabled: false,
      ariaLabel: `Webhook ${webhook.name}`,
      columnsKeys: ['name', 'status', 'url'],
      setColumnsKeys: () => {},
      fields: {
        name: webhook.name,
        url: webhook.url,
        status: webhook.is_active,
        created_at: webhook.created_at,
      },
      setActionsProps: () => {},
      withActions: true,
      onClick: () => {
        uiStore.changeRoute({
          path: `/${IntegrationsRoutes.settings}/${IntegrationsRoutes.root}/${IntegrationsRoutes.webhooks}/${IntegrationsRoutes.edit}/${webhook.id}`,
        })
      },
    }))
  }

  get integration() {
    return this._entities.integrationsStore.getIntegration('webhook')
  }

  get tableStore() {
    return this._tableStore
  }

  get firstLoading() {
    return this._firstLoading
  }

  get webhooks() {
    return this._webhooks
  }

  getWebhook = (id: number) => {
    return this._webhooks.find((webhook) => webhook.id === id)
  }

  updateStatus = async (
    webhook: IResponseExternalWebhook,
    callback?: (webhook: IResponseExternalWebhook) => void
  ) => {
    try {
      await WebhookApi.updateExternalWebhooks(webhook.id, {
        is_active: !webhook.is_active,
        name: webhook.name,
        url: webhook.url,
        events: webhook.events,
      })

      runInAction(() => {
        this._webhooks = this._webhooks.map((currentWebhook) =>
          currentWebhook.id === webhook.id
            ? { ...webhook, is_active: !webhook.is_active }
            : currentWebhook
        )
      })

      callback?.(webhook)

      showToast({
        type: 'success',
        title: 'Webhook was updated successfully',
      })
    } catch (error) {
      logger.error(error)
    }
  }

  updateWebhook = (webhook: IResponseExternalWebhook) => {
    this._webhooks = this._webhooks.map((currentWebhook) =>
      currentWebhook.id === webhook.id ? webhook : currentWebhook
    )
  }

  deleteWebhook = async (webhook: IResponseExternalWebhook) => {
    try {
      await WebhookApi.deleteExternalWebhooks(webhook.id)

      this.loadWebhooks()

      modalStore.closeModal(this._deleteModalId)

      showToast({
        type: 'success',
        title: 'Webhook was deleted successfully',
      })
    } catch (error) {
      showToast({
        type: 'error',
        title: 'Unable to delete the webhook. Please try again.',
      })
      logger.error(error)
    }
  }

  openDeleteModal = async (webhook: IResponseExternalWebhook, callback?: () => void) => {
    modalStore.addModal({
      id: this._deleteModalId,
      type: ModalTypeList.ALERT,
      title: (
        <>
          Delete this webhook?
          <Typography
            variant={'heading-sm'}
            ariaLabel={'title'}
            ellipsis
            className={styles.modalTitle}
          >
            {webhook.name}
          </Typography>
        </>
      ),
      desc: 'This action cannot be undone',
      primaryAction: {
        text: 'Delete',
        onAction: async () => {
          await this.deleteWebhook(webhook)
          callback?.()
        },
      },
      secondaryAction: {
        text: 'Cancel',
        onAction: () => {
          modalStore.closeModal(this._deleteModalId)
        },
      },
    })
  }

  moveToAllWebhooks = () => {
    uiStore.changeRoute({
      path: `/${IntegrationsRoutes.settings}/${IntegrationsRoutes.root}/${IntegrationsRoutes.webhooks}`,
    })
  }

  openDeleteModalWithCloseCallback = (webhook: IResponseExternalWebhook) => {
    this.openDeleteModal(webhook, this.moveToAllWebhooks)
  }

  openDetailsModal = (webhook: IResponseExternalWebhook) => {
    modalStore.addModal({
      id: nanoid(),
      width: 540,
      position: 'right',
      type: ModalTypeList.DEFAULT,
      ModalContentProps: { webhook },
      ModalContent: DetailsModal,
      title: 'Details',
      fullHeight: true,
      pureContent: true,
    })
  }

  onRowClick = (webhook: WebhookRow) => {
    uiStore.changeRoute({
      path: `${IntegrationsRoutes.settings}/${IntegrationsRoutes.root}/${IntegrationsRoutes.webhooks}/${IntegrationsRoutes.history}/${webhook.id}`,
    })
  }

  dispose = () => {
    this._disposeParamsReaction?.()
    this._tableStore.dispose()
    this._webhookHistoryStore.dispose()
  }
}
